/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Helpers for deriving the registrable root domain (eTLD+1) of a brand host or
 * unique battery identifier (UBI) URL, as required by the brand-domain rules
 * (`https://deye.com/01/…` and `https://passport.deye.com/01/…` → `deye.com`).
 */
import {getDomain, getHostname} from 'tldts';

/** Fixed sub-domain prefix the platform suggests when only a root domain is entered. */
export const BRAND_HOST_DEFAULT_PREFIX = 'passport';

/** Maximum length of a fully-qualified hostname (RFC 1035). */
export const HOSTNAME_MAX_LENGTH = 253;

/** RFC 1123 hostname: labels of 1–63 chars, at least two labels. */
export const HOSTNAME_REGEX =
  /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;
/** Dotted-quad IPv4 literal; IPv6 literals never pass {@link HOSTNAME_REGEX} (colons/brackets). */
const IPV4_REGEX = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Normalises user input into a bare lower-case hostname.
 *
 * Accepts `https://Passport.Deye.com/`, `passport.deye.com`, or ` PASSPORT.DEYE.COM ` and
 * returns `passport.deye.com`. Returns `undefined` when no syntactically valid hostname
 * (RFC 1123, at least two labels, not an IP literal) can be extracted.
 *
 * @param {string | undefined} input - Raw host or URL supplied by the caller.
 * @returns {string | undefined} Normalised hostname.
 */
export function normalizeHost(input: string | undefined): string | undefined {
  if (typeof input !== 'string') {
    return undefined;
  }
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) {
    return undefined;
  }
  const candidate = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
  // tldts extracts permissively ("not a host", "a..b.com", IPs); enforce the hostname grammar here
  const hostname = getHostname(candidate);
  return hostname && HOSTNAME_REGEX.test(hostname) && !IPV4_REGEX.test(hostname)
    ? hostname
    : undefined;
}

/**
 * Returns the registrable root domain (eTLD+1) for a hostname or URL.
 *
 * @param {string | undefined} hostOrUrl - Hostname or absolute URL.
 * @returns {string | undefined} Root domain such as `deye.com` or `subaru.co.jp`.
 */
export function toRootDomain(
  hostOrUrl: string | undefined,
): string | undefined {
  const host = normalizeHost(hostOrUrl);
  if (!host) {
    return undefined;
  }
  return getDomain(host) ?? undefined;
}

/**
 * Returns `true` when the host is an apex/root domain (identical to its eTLD+1).
 *
 * @param {string} host - Normalised hostname.
 */
export function isApexDomain(host: string): boolean {
  return toRootDomain(host) === host;
}

/**
 * Builds the platform's suggested brand host for a root domain.
 *
 * @param {string} rootDomain - Registrable domain, e.g. `deye.com`.
 * @returns {string} `passport.deye.com`
 */
export function suggestBrandHost(rootDomain: string): string {
  return `${BRAND_HOST_DEFAULT_PREFIX}.${rootDomain}`;
}

/**
 * Validates a public base URL that is embedded into passport identifiers / QR codes.
 *
 * Accepts only an `https://` origin with a registrable hostname — no credentials, path,
 * query or fragment — so a misconfigured `FRAMEWORK_URL` or a tampered brand host can never
 * be baked into printed identifiers.
 *
 * @param {string | undefined} value - Candidate URL such as `https://passport.deye.com`.
 * @returns {string | undefined} The normalised origin (no trailing slash) or `undefined` if unsafe.
 */
export function parsePublicBaseUrl(
  value: string | undefined,
): string | undefined {
  const url = parseUrl(value);
  if (!url || !isPlainHttpsOrigin(url)) {
    return undefined;
  }
  if (!HOSTNAME_REGEX.test(url.hostname)) {
    return undefined;
  }
  // Rejects IP literals and single-label hosts, which are never valid brand or platform hosts
  if (!getDomain(url.hostname)) {
    return undefined;
  }
  return url.origin;
}

function parseUrl(value: string | undefined): URL | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  try {
    return new URL(value.trim());
  } catch {
    return undefined;
  }
}

/** `https://` origin with no credentials, path, query or fragment. */
function isPlainHttpsOrigin(url: URL): boolean {
  if (url.protocol !== 'https:') {
    return false;
  }
  const hasCredentials = Boolean(url.username || url.password);
  const hasPath = url.pathname !== '' && url.pathname !== '/';
  const hasQueryOrFragment = Boolean(url.search || url.hash);
  return !hasCredentials && !hasPath && !hasQueryOrFragment;
}

/**
 * Like {@link parsePublicBaseUrl} but throws for unsafe values; use at boot and for
 * configuration that must never be silently accepted.
 *
 * @param {string | undefined} value - Candidate URL.
 * @param {string} source - Name of the setting for the error message, e.g. `FRAMEWORK_URL`.
 * @returns {string} The normalised origin.
 * @throws {Error} If the value is not a plain `https://` origin with a registrable hostname.
 */
export function assertPublicBaseUrl(
  value: string | undefined,
  source: string,
): string {
  const origin = parsePublicBaseUrl(value);
  if (!origin) {
    throw new Error(
      `${source} must be a plain https:// origin with a registrable hostname (no credentials, path, query or fragment); got "${value}"`,
    );
  }
  return origin;
}
